import { useEffect } from 'react';

/**
 * `UserHubClient` — realtime WebSocket consumer (Wave 3.1 implementation).
 *
 * Per `plans/03-architecture/realtime.md`:
 *   - One `UserHubDO` per user; clients open a WebSocket to
 *     `wss://api.barel.app/api/v1/realtime`.
 *   - Web auth via session cookie (transparent on `WebSocket(...)`).
 *   - Extension auth via `?token=<bearer>` query param (since browser
 *     extensions can't set custom headers on `WebSocket()` from the
 *     content script side).
 *   - Each event has monotonic `seq`; reconnect with `?since=<seq>` to
 *     replay buffered events. Stale `since` returns `{ kind: 'resync' }`.
 *
 * The constructor surface is stable from Wave 2 (the `type`-keyed event
 * union remains the public shape) — Wave 3.1 only *adds* protocol
 * support without breaking existing consumers. Internally we accept both
 * the legacy `type` field and the canonical `kind` field; the client
 * normalizes to `type` for downstream `onMessage` callbacks.
 */

/* ─── Event types — Wave 3.1 expanded union ─────────────────────────────── */

/** Server-emitted event kinds (mirrors `apps/api/src/do/userHubProto.ts`). */
export const USER_HUB_EVENT_KINDS = [
  'message.in',
  'message.out',
  'draft.ready',
  'draft.streaming',
  'draft.sent',
  'account.connected',
  'account.status',
  'campaign.run.update',
  'relance.scheduled',
  'relance.sent',
  'relance.cancelled',
  'lead.pushed',
  'lead.crm-synced',
  'subscription.updated',
  'presence',
] as const;

export type UserHubEventKind = (typeof USER_HUB_EVENT_KINDS)[number];

export interface UserHubConnectedEvent {
  type: 'connected';
  correlationId: string;
  /** Last sequence number known to the server for this user's hub. */
  lastSeq?: number;
}

/** `hello` frame from the DO immediately after `acceptWebSocket`. */
export interface UserHubHelloEvent {
  type: 'hello';
  /** Server-side wsId, useful for log correlation. */
  wsId: string;
  lastSeq: number;
  serverTime: string;
}

export interface UserHubResyncEvent {
  type: 'resync';
  message: 'reload-required';
}

export interface UserHubServerPing {
  type: 'ping';
  ts?: number;
}

export interface UserHubServerPong {
  type: 'pong';
  ts?: number;
}

export interface UserHubGenericEvent {
  type: string;
  seq?: number;
  topic?: string;
  payload?: unknown;
  [key: string]: unknown;
}

export type UserHubServerEvent =
  | UserHubConnectedEvent
  | UserHubHelloEvent
  | UserHubResyncEvent
  | UserHubServerPing
  | UserHubServerPong
  | UserHubGenericEvent;

/** Client → server message shape. Both `type` and `kind` accepted. */
export interface UserHubClientEvent {
  type: string;
  [key: string]: unknown;
}

/* ─── Options ───────────────────────────────────────────────────────────── */

export interface UserHubReconnectOptions {
  enabled?: boolean;
  baseDelayMs?: number;
  maxDelayMs?: number;
  maxAttempts?: number;
  /** ±jitter as a fraction of the computed delay; default `0.25` (±25%). */
  jitterFraction?: number;
}

export interface UserHubClientOptions {
  /** API base URL — `https://…` is rewritten to `wss://…` automatically. */
  baseUrl: string;
  /** Mounted realtime path — defaults to `/api/v1/realtime`. */
  path?: string;
  /**
   * Bearer token retrieval — extension uses this; web auth rides on the
   * session cookie and can leave this undefined.
   */
  authToken?: () => string | Promise<string>;
  /**
   * Resume marker. Client tracks the highest `seq` it observed; on reconnect
   * we send `?since=<seq>` so the DO can replay buffered events.
   */
  getLastSeq?: () => number | undefined;
  /** Server-event handler. */
  onMessage: (msg: UserHubServerEvent) => void;
  /** Open-state handler. */
  onOpen?: () => void;
  /** Close handler — fires once on terminal disconnect (after retries). */
  onClose?: (code: number, reason: string) => void;
  /** Transport-level error handler. */
  onError?: (err: Error) => void;
  reconnect?: UserHubReconnectOptions;
  /** Heartbeat interval ms (default 30 000 per realtime.md §Backpressure). */
  heartbeatMs?: number;
  /**
   * Override for tests — pass a `WebSocket` ctor or an instance factory.
   * Defaults to platform `WebSocket`.
   */
  webSocketImpl?: typeof WebSocket;
}

/* ─── Client ────────────────────────────────────────────────────────────── */

const RECONNECT_DEFAULTS: Required<UserHubReconnectOptions> = {
  enabled: true,
  // Wave 3.1 contract: base 1s, max 60s, jitter ±25% per realtime.md /
  // wave-3 prompt. Tests can still override via constructor options.
  baseDelayMs: 1_000,
  maxDelayMs: 60_000,
  maxAttempts: Infinity,
  jitterFraction: 0.25,
};

/**
 * Build the `wss://…` URL for the realtime endpoint, layering on the
 * optional bearer token + resume seq query params.
 */
export function buildRealtimeUrl(
  baseUrl: string,
  path: string,
  token?: string,
  since?: number,
): string {
  // Empty baseUrl is the dev shape — web hits the API through the Vite
  // proxy on its own origin, so default to `window.location.origin` so the
  // resulting URL is absolute (the WebSocket constructor rejects relative).
  const effectiveBase =
    baseUrl || (typeof globalThis !== 'undefined' && globalThis.location?.origin) || '';
  // http -> ws, https -> wss
  const wsBase = effectiveBase.replace(/^http(s?):/, 'ws$1:');
  const url = new URL(`${wsBase}${path}`);
  if (token) url.searchParams.set('token', token);
  if (typeof since === 'number') url.searchParams.set('since', String(since));
  return url.toString();
}

/** Subscriber callback signature for `on(eventType, cb)`. */
export type UserHubListener = (evt: UserHubServerEvent) => void;

export interface UserHubRingEntry {
  seq: number;
  receivedAt: number;
  event: UserHubServerEvent;
}

export class UserHubClient {
  private ws: WebSocket | null = null;
  private readonly opts: UserHubClientOptions;
  private readonly reconnectCfg: Required<UserHubReconnectOptions>;
  private readonly WSImpl: typeof WebSocket;
  private readonly heartbeatMs: number;

  private attempt = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pendingSends: UserHubClientEvent[] = [];
  private isClosing = false;
  private lastSeq: number | undefined;

  /** Per-event-type listeners — used by `useUserHubEvent`. */
  private listeners = new Map<string, Set<UserHubListener>>();
  /** Topics the client wants to be subscribed to (re-asserted on reconnect). */
  private subscribedTopics = new Set<string>();
  /** Last 50 events received — debug + reconnect dedup. */
  private ring: UserHubRingEntry[] = [];
  private readonly ringCapacity = 50;

  constructor(opts: UserHubClientOptions) {
    this.opts = opts;
    this.reconnectCfg = { ...RECONNECT_DEFAULTS, ...(opts.reconnect ?? {}) };
    this.heartbeatMs = opts.heartbeatMs ?? 30_000;
    const impl = opts.webSocketImpl ?? (globalThis as { WebSocket?: typeof WebSocket }).WebSocket;
    if (!impl) {
      throw new Error('UserHubClient: no WebSocket implementation available.');
    }
    this.WSImpl = impl;
    this.lastSeq = opts.getLastSeq?.();
  }

  /** Open the connection. Resolves when the WS reaches `OPEN`. */
  async connect(): Promise<void> {
    this.isClosing = false;
    return this.openOnce();
  }

  /**
   * Send a client event. If the socket isn't `OPEN` yet, the message is
   * queued and flushed on `open`. Caller can `close()` to drop the queue.
   */
  send(msg: UserHubClientEvent): void {
    if (this.ws && this.ws.readyState === this.WSImpl.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else {
      this.pendingSends.push(msg);
    }
  }

  /** Subscribe to one or more server-side topics (e.g. `conv:<id>`). */
  subscribe(topics: string[] | string): void {
    const list = Array.isArray(topics) ? topics : [topics];
    if (list.length === 0) return;
    for (const t of list) this.subscribedTopics.add(t);
    this.send({ type: 'subscribe', kind: 'subscribe', topics: list });
  }

  /** Unsubscribe from one or more topics. */
  unsubscribe(topics: string[] | string): void {
    const list = Array.isArray(topics) ? topics : [topics];
    if (list.length === 0) return;
    for (const t of list) this.subscribedTopics.delete(t);
    this.send({ type: 'unsubscribe', kind: 'unsubscribe', topics: list });
  }

  /**
   * Per-event-type listener. Returns an unsubscribe function. Used by the
   * React `useUserHubEvent` hook and any vanilla consumer (extension).
   */
  on(type: string, cb: UserHubListener): () => void {
    let bag = this.listeners.get(type);
    if (!bag) {
      bag = new Set();
      this.listeners.set(type, bag);
    }
    bag.add(cb);
    return () => {
      const current = this.listeners.get(type);
      if (!current) return;
      current.delete(cb);
      if (current.size === 0) this.listeners.delete(type);
    };
  }

  /** Snapshot of the recent-event ring (read-only). */
  getRing(): readonly UserHubRingEntry[] {
    return this.ring.slice();
  }

  /** Current `lastSeq` observed (undefined before any event). */
  getLastSeq(): number | undefined {
    return this.lastSeq;
  }

  /** Close the socket and stop reconnecting. */
  close(code = 1000, reason = 'client_close'): void {
    this.isClosing = true;
    this.stopHeartbeat();
    this.pendingSends = [];
    if (this.ws) {
      try {
        this.ws.close(code, reason);
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  /** Current readyState (or -1 if no socket). */
  get readyState(): number {
    return this.ws?.readyState ?? -1;
  }

  /* ─── Internal ──────────────────────────────────────────────────────── */

  private async openOnce(): Promise<void> {
    const token = await Promise.resolve(this.opts.authToken?.());
    const url = buildRealtimeUrl(
      this.opts.baseUrl,
      this.opts.path ?? '/api/v1/realtime',
      token,
      this.lastSeq,
    );

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let ws: WebSocket;
      try {
        ws = new this.WSImpl(url);
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      this.ws = ws;

      ws.onopen = () => {
        this.attempt = 0;
        this.startHeartbeat();
        // Re-assert any topic subscriptions the consumer set before/while
        // disconnected. This goes onto the wire ahead of any pending sends.
        if (this.subscribedTopics.size > 0) {
          try {
            ws.send(
              JSON.stringify({
                type: 'subscribe',
                kind: 'subscribe',
                topics: Array.from(this.subscribedTopics),
              }),
            );
          } catch {
            /* socket already gone — onclose will handle */
          }
        }
        // Flush queued sends in order.
        const pending = this.pendingSends;
        this.pendingSends = [];
        for (const msg of pending) {
          try {
            ws.send(JSON.stringify(msg));
          } catch {
            // re-queue and bail; reconnect will retry.
            this.pendingSends.push(msg);
            break;
          }
        }
        this.opts.onOpen?.();
        if (!settled) {
          settled = true;
          resolve();
        }
      };

      ws.onmessage = (event: MessageEvent) => {
        let data: unknown;
        try {
          data = typeof event.data === 'string' ? JSON.parse(event.data) : (event.data as unknown);
        } catch {
          // Not JSON — ignore silently (heartbeat pong frames, e.g.).
          return;
        }
        if (data && typeof data === 'object') {
          const raw = data as Record<string, unknown>;
          // Normalize: server uses `kind`, legacy clients may emit `type`.
          // We surface `type` to consumers and preserve `kind` for round-tripping.
          if (typeof raw['type'] !== 'string' && typeof raw['kind'] === 'string') {
            raw['type'] = raw['kind'];
          }
          const evt = raw as UserHubServerEvent;
          if (typeof (evt as UserHubGenericEvent).seq === 'number') {
            this.lastSeq = (evt as UserHubGenericEvent).seq;
            this.pushRing(evt);
          }
          // Per-type listeners run before the global onMessage so consumers
          // can filter early without touching the global path.
          const bag = this.listeners.get((evt as { type: string }).type);
          if (bag) {
            for (const cb of bag) {
              try {
                cb(evt);
              } catch {
                /* listener error — never break the dispatch loop */
              }
            }
          }
          this.opts.onMessage(evt);
        }
      };

      ws.onerror = (event: Event) => {
        const err =
          event instanceof ErrorEvent
            ? event.error instanceof Error
              ? event.error
              : new Error(event.message ?? 'WebSocket error')
            : new Error('WebSocket error');
        this.opts.onError?.(err);
        if (!settled) {
          settled = true;
          reject(err);
        }
      };

      ws.onclose = (event: CloseEvent) => {
        this.stopHeartbeat();
        this.ws = null;
        if (this.isClosing) {
          this.opts.onClose?.(event.code, event.reason);
          return;
        }
        if (this.reconnectCfg.enabled && this.attempt < this.reconnectCfg.maxAttempts) {
          this.attempt += 1;
          const delay = this.computeReconnectDelay();
          setTimeout(() => {
            if (!this.isClosing) {
              void this.openOnce().catch(() => {
                /* swallow — onerror surfaces it */
              });
            }
          }, delay);
        } else {
          this.opts.onClose?.(event.code, event.reason);
        }
      };
    });
  }

  private computeReconnectDelay(): number {
    const exp = this.reconnectCfg.baseDelayMs * Math.pow(2, this.attempt - 1);
    const capped = Math.min(this.reconnectCfg.maxDelayMs, exp);
    const jitter = capped * this.reconnectCfg.jitterFraction * (Math.random() * 2 - 1);
    return Math.max(0, Math.round(capped + jitter));
  }

  private pushRing(evt: UserHubServerEvent): void {
    const seq = (evt as UserHubGenericEvent).seq;
    if (typeof seq !== 'number') return;
    this.ring.push({ seq, receivedAt: Date.now(), event: evt });
    if (this.ring.length > this.ringCapacity) {
      this.ring.splice(0, this.ring.length - this.ringCapacity);
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    if (this.heartbeatMs <= 0) return;
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === this.WSImpl.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: 'ping', kind: 'ping', ts: Date.now() }));
        } catch {
          /* socket already torn down */
        }
      }
    }, this.heartbeatMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}

/* ─── React hook ─────────────────────────────────────────────────────────── */

/**
 * Internal hook factory — kept separate from the class so consumers who
 * never use React (e.g. the extension service worker) tree-shake it out.
 *
 * Wave 4 will wire `<UserHubProvider>` so `useUserHubEvent` resolves the
 * client from React context. For Wave 3.1 we accept the client as a first
 * arg; the provider wraps that with no-arg ergonomics.
 */

/**
 * `useUserHubEvent('message.in', cb)` — re-subscribes the callback whenever
 * `cb` identity changes. The hook also takes care of cleaning up on
 * unmount (no leaks across route transitions).
 *
 * @example
 * useUserHubEvent(client, 'message.in', (evt) => {
 *   queryClient.invalidateQueries({ queryKey: ['conversations'] });
 * });
 */
export function useUserHubEvent(
  client: UserHubClient | null | undefined,
  type: string,
  cb: UserHubListener,
): void {
  useEffect(() => {
    if (!client) return;
    const unsub = client.on(type, cb);
    return () => {
      unsub();
    };
  }, [client, type, cb]);
}
